import { Button, Dialog, DialogContentText } from "@mui/material"
import { LanguageContext } from "context/LanguageContext"
import { clearBasket } from "modules/basket/actions"
import { clearCateringSchedule } from "modules/products/actions"
import { setAddress } from "modules/location/actions"
import { getCDNURL } from "network/api/networkSettings"
import { useContext, useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { getUtmSearch } from "utils/parse"
import { clearCateringData } from "utils/cateringStorage"
import cateringSwitchImage from '../../../assets/images/cateringSwitch.png'
import { 
    trackCateringUserBehaviorClearCartFlowModalOpened,
    trackCateringUserBehaviorClearCartFlowContinueOrder,
    trackCateringUserBehaviorClearCartFlowClearCart,
    trackCateringUserBehaviorClearCartFlowCartCleared 
} from 'middleware/helper'
import { util } from 'utils'
import './index.scss'

export const ModeSwitchPopUp = ({ 
    open, 
    onClose, 
    targetMode, // 'shop' or 'catering'
    search = '' 
}) => {
    const { strings } = useContext(LanguageContext)
    const cartPatchInProgress = useSelector(state => state.basket.getIn(['cartPatchInProgress']))
    const basketItems = useSelector(state => state.basket.getIn(['basketItems']))
    const basketResponse = useSelector(state => state.basket.get('basketResponse'))
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [clearingCart, setClearingCart] = useState(false)
    const patchStarted = useRef(false)
    const hasTrackedModalOpen = useRef(false)

    // Track modal opened when it first opens
    useEffect(() => {
        if (open && !hasTrackedModalOpen.current && basketItems) {
            const itemsCount = util.getTotalItems(basketItems)
            const cartValue = basketResponse?.toJS()?.totalBeforeTax?.value || 0
            const sourcePage = window.location.pathname
            
            trackCateringUserBehaviorClearCartFlowModalOpened(itemsCount, sourcePage)
            hasTrackedModalOpen.current = true
        }
        
        // Reset tracking flag when modal closes
        if (!open) {
            hasTrackedModalOpen.current = false
        }
    }, [open, basketItems, basketResponse])

    const onConfirmClick = (e) => {
        e.preventDefault()
        
        // Track clear cart action
        const itemsCount = basketItems ? util.getTotalItems(basketItems) : 0
        const cartValue = basketResponse?.toJS()?.totalBeforeTax?.value || 0
        
        trackCateringUserBehaviorClearCartFlowClearCart(itemsCount, cartValue)
        
        // Set clearing cart state to show loader
        patchStarted.current = false
        setClearingCart(true)
        
        // Dispatch immediately - CLEAR_BASKET sets cartPatchInProgress=true in reducer
        dispatch(clearBasket())
        
        // Track cart cleared
        trackCateringUserBehaviorClearCartFlowCartCleared(itemsCount, cartValue)
    }
    
    const handleContinueOrder = (e) => {
        const itemsCount = basketItems ? util.getTotalItems(basketItems) : 0
        trackCateringUserBehaviorClearCartFlowContinueOrder(itemsCount, 'continued')
        onClose(e)
    }

    // Track when cartPatchInProgress goes true (patch has started)
    useEffect(() => {
        if (clearingCart && cartPatchInProgress) {
            patchStarted.current = true
        }
    }, [clearingCart, cartPatchInProgress])

    // Only close when patch has started AND then finished
    useEffect(() => {
        if (clearingCart && patchStarted.current && !cartPatchInProgress) {
            patchStarted.current = false
            setClearingCart(false)
            onClose()
            setTimeout(() => {
                const targetPath = targetMode === 'catering' ? '/catering' : targetMode === 'home' ? '/home' : '/shop'
                navigate(`${targetPath}${getUtmSearch(search)}`)
            }, 300)
        }
    }, [clearingCart, cartPatchInProgress, targetMode, navigate, search, onClose])

    return (
        <Dialog
            open={open || clearingCart} // Keep dialog open during cart clearing
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && !clearingCart) {
                    onClose(event, reason)
                }
            }}
            maxWidth="xs"
            fullWidth
            disableEscapeKeyDown={true}
            PaperProps={{ className: 'mode-switch__dialog-paper' }}
            onClick={(e) => e.preventDefault()}
        >
            {(clearingCart || cartPatchInProgress) ? (
                <div className='mode-switch__dialog-loader'>
                    <img
                        className={'mode-switch__dialog-loader-content'}
                        src={getCDNURL() + 'assets/gifs/loader.gif'}
                        alt={strings.loading}
                    />
                </div>
            ) : (
                <>
                    <div className="mode-switch__dialog-content">
                        <div className="mode-switch__dialog-image-container">
                            <img 
                                src={cateringSwitchImage} 
                                alt="Mode switch icon" 
                                className="mode-switch__dialog-icon"
                            />
                        </div>
                        <DialogContentText className="mode-switch__dialog-content-text">
                            {strings.modeSwitchPopUpMsg}
                        </DialogContentText>
                        <div className="mode-switch__dialog-action-btns">
                            <Button
                                onClick={handleContinueOrder}
                                className="mode-switch__dialog-button mode-switch__dialog-button-primary"
                                fullWidth
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    padding: '16px',
                                    borderRadius: '40px',
                                    fontSize: '16px',
                                    width: '100%',
                                    height: '45px',
                                    color: '#fff',
                                    backgroundColor: '#00805F',
                                    border: '1px solid #00805F',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '&:hover': {
                                        backgroundColor: '#006b4f',
                                        border: '1px solid #006b4f',
                                    },
                                    '@media (max-width: 600px)': {
                                        fontSize: '14px',
                                        padding: '12px',
                                        height: '48px',
                                    }
                                }}
                            >
                                {strings.continueCurrentOrder}
                            </Button>
                            <Button
                                className="mode-switch__dialog-button mode-switch__dialog-button-secondary"
                                onClick={onConfirmClick}
                                fullWidth
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    padding: '16px',
                                    borderRadius: '40px',
                                    fontSize: '16px',
                                    width: '100%',
                                    height: '45px',
                                    color: '#00805F',
                                    backgroundColor: '#fff',
                                    border: '2px solid #00805F',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                    },
                                    '@media (max-width: 600px)': {
                                        fontSize: '14px',
                                        padding: '12px',
                                        height: '48px',
                                        border: '1px solid #00805F',
                                    }
                                }}
                            >
                                {strings.clearBasketAndStart}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </Dialog>
    )
}

export default ModeSwitchPopUp
